import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { getAllColors } from '../../api/color';
import type { Color } from '../../api/color';
import type { Diocese } from '../../api/diocese';
import { getAllDioceses } from '../../api/diocese';
import { getAllFabrics } from '../../api/fabric';
import type { Fabric } from '../../api/fabric';
import { getAllLegacyCollars } from '../../api/legacyCollar';
import type { LegacyCollar } from '../../api/legacyCollar';
import type { MaterialColor } from '../../api/materialColor';
import { getAllMaterialTypes } from '../../api/materialType';
import type { MaterialType } from '../../api/materialType';
import { getAllSizes } from '../../api/size';
import type { Size } from '../../api/size';
import { getAllVestments } from '../../api/vestment';
import type { Vestment } from '../../api/vestment';
import { MaterialKind } from '../enum/materialKind';

export interface AppContextValue {
  dioceses: Diocese[];
  vestments: Vestment[];
  sizes: Size[];
  collars: LegacyCollar[];
  fabrics: Fabric[];
  materialTypes: MaterialType[];
  threads: MaterialColor[];
  trimmings: MaterialColor[];
  galoons: MaterialColor[];
  cords: MaterialColor[];
  tassels: MaterialColor[];
  colors: Color[];
  refreshContext: () => Promise<void>;
}

function getMaterialColors(
  types: MaterialType[],
  kind: MaterialKind
): MaterialColor[] {
  return types
    .filter((type) => type.kind === kind)
    .flatMap((type) =>
      type.colors.map((color) => ({ ...color, typeName: type.name }))
    );
}

const AppContext = createContext<AppContextValue>({
  dioceses: [],
  vestments: [],
  sizes: [],
  collars: [],
  fabrics: [],
  materialTypes: [],
  threads: [],
  trimmings: [],
  galoons: [],
  cords: [],
  tassels: [],
  colors: [],
  refreshContext: () => Promise.resolve(),
});

export function useAppContext(): AppContextValue {
  return useContext(AppContext);
}

const AppContextProvider = AppContext.Provider;

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [dioceses, setDioceses] = useState<Diocese[]>([]);
  const [vestments, setVestments] = useState<Vestment[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [collars, setCollars] = useState<LegacyCollar[]>([]);
  const [fabrics, setFabrics] = useState<Fabric[]>([]);
  const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
  const [threads, setThreads] = useState<MaterialColor[]>([]);
  const [trimmings, setTrimmings] = useState<MaterialColor[]>([]);
  const [galoons, setGaloons] = useState<MaterialColor[]>([]);
  const [cords, setCords] = useState<MaterialColor[]>([]);
  const [tassels, setTassels] = useState<MaterialColor[]>([]);
  const [colors, setColors] = useState<Color[]>([]);

  const refreshContext = useCallback(async () => {
    try {
      const [
        diocesesData,
        vestmentsData,
        sizesData,
        collarsData,
        fabricsData,
        materialTypesData,
        colorsData,
      ] = await Promise.all([
        getAllDioceses(),
        getAllVestments(),
        getAllSizes(),
        getAllLegacyCollars(),
        getAllFabrics(),
        getAllMaterialTypes(),
        getAllColors(),
      ]);
      setDioceses(diocesesData);
      setVestments(vestmentsData);
      setSizes(sizesData);
      setCollars(collarsData);
      setFabrics(fabricsData);
      setMaterialTypes(materialTypesData);
      setThreads(getMaterialColors(materialTypesData, MaterialKind.Thread));
      setTrimmings(getMaterialColors(materialTypesData, MaterialKind.Trimming));
      setGaloons(getMaterialColors(materialTypesData, MaterialKind.Galoon));
      setCords(getMaterialColors(materialTypesData, MaterialKind.Cord));
      setTassels(getMaterialColors(materialTypesData, MaterialKind.Tassel));
      setColors(colorsData);
    } catch {
      toast.error(
        'Failed to load vestments, sizes, collars, fabrics, threads, trimmings, galoons, cords, tassels and colors'
      );
    }
  }, []);

  useEffect(() => {
    void refreshContext();
  }, [refreshContext]);

  return (
    <AppContextProvider
      value={{
        dioceses,
        vestments,
        sizes,
        collars,
        fabrics,
        materialTypes,
        threads,
        trimmings,
        galoons,
        cords,
        tassels,
        colors,
        refreshContext,
      }}
    >
      {children}
    </AppContextProvider>
  );
}
